import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { IS_ENV_DEV } from "./environment";
import { ServerError } from "./types";

const baseURL =
  window.location.hostname === "localhost"
    ? "http://localhost:3002"
    : `${window.location.protocol}//${window.location.hostname.replace(/^[^.]+\./, "api.")}`;

const axiosClient = axios.create({ baseURL, withCredentials: !IS_ENV_DEV });

const normalizeUrl = (url: string) => {
  return url.endsWith("/") ? url.slice(0, -1) : url;
};

axiosClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");

    const isAuthRequest = config.url?.includes("auth");

    if (!isAuthRequest && !token && !refreshToken) {
      window.location.href = "/login";
    }

    // TODO: TO BE CHANGED TO SMART-DRAFTER
    config.headers["x-app-name"] = "DRAFTING_TOOL";

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return error;
  }
);

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// Function to subscribe new requests to the token refresh process
const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Function to notify subscribers once a new token is received
const onRefreshed = (newToken: string) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = []; // Clear the queue
};

axiosClient.interceptors.response.use(
  (response) => response, // If response is successful, return it
  async (error) => {
    const originalRequest = error.config;

    // Check for 401 error and retry flag
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem("refreshToken");

      if (refreshToken) {
        if (!isRefreshing) {
          isRefreshing = true;

          try {
            // Call the refresh token endpoint to get a new access token
            const { data } = await axios.post(
              `${normalizeUrl(baseURL)}/auth/refreshToken`,
              {
                token: refreshToken,
              },
              {
                headers: {
                  "x-app-name": "DRAFTING_TOOL",
                },
              }
            );

            const { accessToken } = data;

            // Update the access token in local storage and set it in axios headers
            localStorage.setItem("accessToken", accessToken);

            // Retry the original request with the new access token
            originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
            onRefreshed(accessToken);

            isRefreshing = false;
            return axios(originalRequest);
          } catch (error) {
            // Handle refresh token failure (e.g., redirect to login)
            console.error("Refresh token failed", error);
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            window.location.href = "/login";
            isRefreshing = false;
            return error;
          }
        } else {
          // If a refresh is already in progress, queue the request
          return new Promise((resolve) => {
            subscribeTokenRefresh((newToken: string) => {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              resolve(axios(originalRequest));
            });
          });
        }
      }
    }

    throw error;
  }
);

export type QueryArg = AxiosRequestConfig & {
  workspaceId?: string;
};

export const axiosBaseQuery =
  <
    ResultType = unknown, // Generic type for the API response
    ErrorType = ServerError | undefined, // Generic type for the error
  >(
    { baseUrl }: { baseUrl: string } = { baseUrl: "" }
  ): BaseQueryFn<QueryArg, ResultType, ErrorType> =>
  async ({ url, method, data, params, workspaceId, ...rest }) => {
    try {
      const result = await axiosClient<ResultType>({
        url: baseUrl + url,
        method,
        data: data,
        params: params,
        headers: {
          "x-workspace-id": workspaceId,
        },
        ...rest,
      });
      return { data: result.data };
    } catch (error) {
      const axiosError = error as AxiosError<ErrorType>;
      const errorResponse = axiosError.response?.data;
      return { error: errorResponse as ErrorType };
    }
  };

export default axiosClient;
